import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "adminPassword";

export function useAdminAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(async (inputPassword) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password: inputPassword });
      if (response.data.success) {
        setIsAuthenticated(true);
        setPassword(inputPassword);
        sessionStorage.setItem(STORAGE_KEY, inputPassword);
        toast.success("Inloggning lyckades");
        return true;
      }
      return false;
    } catch {
      toast.error("Fel lösenord");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setPassword("");
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const getStoredPassword = useCallback(() => {
    return sessionStorage.getItem(STORAGE_KEY);
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const savedPassword = sessionStorage.getItem(STORAGE_KEY);
    if (savedPassword) {
      setPassword(savedPassword);
      setIsAuthenticated(true);
    }
  }, []);

  return {
    isAuthenticated,
    password,
    isLoading,
    login,
    logout,
    getStoredPassword,
    setIsLoading
  };
}

export function useAdminData(password, isAuthenticated) {
  const [referrals, setReferrals] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [codes, setCodes] = useState([]);
  const [analyticsDays, setAnalyticsDays] = useState(null); // null = all time
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);

  const fetchAnalytics = useCallback(async (pwd, days) => {
    if (!pwd) return;
    setIsAnalyticsLoading(true);
    try {
      const query = days ? `&days=${days}` : "";
      const res = await axios.get(`${API}/admin/analytics?password=${pwd}${query}`);
      setAnalytics(res.data);
    } catch {
      toast.error("Kunde inte hämta analys");
    } finally {
      setIsAnalyticsLoading(false);
    }
  }, []);

  const changeAnalyticsRange = useCallback((days, pwd) => {
    setAnalyticsDays(days);
    fetchAnalytics(pwd, days);
  }, [fetchAnalytics]);

  const fetchData = useCallback(async (pwd) => {
    if (!pwd) return;
    setIsLoading(true);
    try {
      const [referralsRes, ambassadorsRes, statsRes, analyticsRes, codesRes] = await Promise.all([
        axios.get(`${API}/admin/ticket-referrals?password=${pwd}`),
        axios.get(`${API}/admin/ambassadors?password=${pwd}`),
        axios.get(`${API}/admin/stats?password=${pwd}`),
        axios.get(`${API}/admin/analytics?password=${pwd}`),
        axios.get(`${API}/admin/codes?password=${pwd}`)
      ]);
      setReferrals(referralsRes.data);
      setAmbassadors(ambassadorsRes.data);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setCodes(codesRes.data);
    } catch {
      toast.error("Kunde inte hämta data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated && password) {
      fetchData(password);
    }
  }, [isAuthenticated, password, fetchData]);

  const updateAmbassadorStatus = useCallback(async (ambassadorId, newStatus, pwd, customCode = null) => {
    try {
      const payload = { status: newStatus };
      if (customCode) {
        payload.custom_code = customCode;
      }
      await axios.put(`${API}/admin/ambassador/${ambassadorId}/status?password=${pwd}`, payload);
      toast.success(newStatus === "approved" ? "Ambassadör godkänd!" : newStatus === "rejected" ? "Ambassadör nekad" : "Status uppdaterad");
      fetchData(pwd);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Kunde inte uppdatera status";
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchData]);

  const fetchCodes = useCallback(async (pwd) => {
    try {
      const res = await axios.get(`${API}/admin/codes?password=${pwd}`);
      setCodes(res.data);
    } catch {
      toast.error("Kunde inte hämta koder");
    }
  }, []);

  const createCode = useCallback(async (code, name, pwd) => {
    try {
      await axios.post(`${API}/admin/codes?password=${pwd}`, { code, name });
      toast.success(`Kod ${code.toUpperCase()} skapad!`);
      await fetchCodes(pwd);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Kunde inte skapa kod";
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchCodes]);

  const deleteCode = useCallback(async (code, pwd) => {
    try {
      await axios.delete(`${API}/admin/codes/${encodeURIComponent(code)}?password=${pwd}`);
      toast.success(`Kod ${code} borttagen`);
      await fetchCodes(pwd);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Kunde inte ta bort kod";
      toast.error(message);
      return { success: false, error: message };
    }
  }, [fetchCodes]);

  return {
    referrals,
    ambassadors,
    stats,
    analytics,
    codes,
    isLoading,
    isAnalyticsLoading,
    analyticsDays,
    changeAnalyticsRange,
    createCode,
    deleteCode,
    fetchData,
    updateAmbassadorStatus
  };
}

export function useAdminExport() {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(async (endpoint, filename, password) => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}${endpoint}?password=${password}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exporterad!");
    } catch {
      toast.error("Kunde inte exportera CSV");
    } finally {
      setIsExporting(false);
    }
  }, []);

  const exportReferrals = useCallback((password) => {
    return exportCSV('/admin/ticket-referrals/export', 'dunderkryssningen_referrals.csv', password);
  }, [exportCSV]);

  const exportAmbassadors = useCallback((password) => {
    return exportCSV('/admin/ambassadors/export', 'dunderkryssningen_ambassadors.csv', password);
  }, [exportCSV]);

  return {
    isExporting,
    exportReferrals,
    exportAmbassadors
  };
}
